import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Shared hover-spotlight hook for calendar block renderers
 * (WeekModal / DayModal / DayViewModal).
 *
 * Behavior per user spec:
 *   - Hovering any block fades every OTHER block to opacity 0.4.
 *   - Chain members of the hovered block also stay at opacity 1 so the
 *     whole chain lights up together.
 *   - Hovered self gets z-index 51, chain members z-index 50 so two
 *     chained blocks that overlap at the same time slot stack correctly.
 *
 * Timing refinements (second pass — prevents flicker on fast cursor motion):
 *   - Activation is DEBOUNCED by 300ms. If the user sweeps the mouse
 *     across blocks without pausing, the spotlight never activates.
 *   - Deactivation has a 150ms GRACE period. If the user moves from one
 *     block to another within 150ms, the spotlight swaps to the new
 *     block without flashing through the "nothing hovered" state.
 *   - Active-state swap is instant: once the spotlight is on, moving to
 *     another block re-targets it with no additional 300ms wait.
 *   - Opacity transition is 300ms (twice the original 150ms) so the fade
 *     is slower and easier to follow with the eye.
 *
 * Usage:
 *   const { onEnter, onLeave, styleFor, isHoverActive } = useCalendarHoverHighlight();
 *   <div
 *     onMouseEnter={() => onEnter(block)}
 *     onMouseLeave={onLeave}
 *     style={{ ...block.style, ...styleFor(block) }}
 *   />
 *
 * `isHoverActive` is `true` when a block is in the spotlight (after the
 * 300 ms enter debounce fires). Consumers like the current-time line use
 * it to dim themselves to the same `0.4` non-hovered alpha so the hover
 * focus stays dominant. Reads as a boolean derived from `hoveredId !==
 * null` — exposed as a separate name so callers don't reach into hook
 * internals.
 */
export function useCalendarHoverHighlight() {
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredChainGroupId, setHoveredChainGroupId] = useState(null);
  // Refs for the timers + active-state flag. Refs (not state) because
  // updating them should NOT trigger a re-render — only the actual
  // hoveredId/chain changes should.
  const activeRef = useRef(false);
  const enterTimerRef = useRef(null);
  const swapTimerRef = useRef(null);
  const leaveTimerRef = useRef(null);

  // Timer cleanup on unmount so a modal close with a pending debounce
  // doesn't fire setState on an unmounted component.
  useEffect(() => {
    return () => {
      if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
      if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, []);

  const applyBlockAsHovered = useCallback((block) => {
    const id = block?.item?.calendarItem?.id || null;
    const chainId =
      block?.item?.calendarItem?.chainGroupId &&
      (block?.item?.calendarItem?.eventType || "job") !== "vacation"
        ? String(block.item.calendarItem.chainGroupId)
        : null;
    setHoveredId(id);
    setHoveredChainGroupId(chainId);
  }, []);

  const onEnter = useCallback(
    (block) => {
      // Cancel any pending leave — user re-entered a block before the
      // deactivation fired.
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
      if (activeRef.current) {
        // Already in spotlight mode — debounce the target swap by 80ms so
        // a fast cursor sweep across many blocks collapses into a single
        // setState when the cursor finally pauses. Prevents the React
        // re-render storm that made the effect feel stuttery.
        if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
        swapTimerRef.current = setTimeout(() => {
          swapTimerRef.current = null;
          applyBlockAsHovered(block);
        }, 80);
        return;
      }
      // Not yet active — longer 300ms debounce to prevent activation on
      // quick sweeps. Only a real hover (pause) lights it up.
      if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
      enterTimerRef.current = setTimeout(() => {
        enterTimerRef.current = null;
        activeRef.current = true;
        applyBlockAsHovered(block);
      }, 300);
    },
    [applyBlockAsHovered]
  );

  const onLeave = useCallback(() => {
    // User left before the enter debounce fired — cancel the pending
    // activation so it doesn't pop on later.
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    // Also cancel any pending swap — the user may be sweeping off-calendar
    // entirely, and we don't want a stale swap to fire after deactivate.
    if (swapTimerRef.current) {
      clearTimeout(swapTimerRef.current);
      swapTimerRef.current = null;
    }
    if (!activeRef.current) return;
    // Grace before deactivating — if the cursor lands on another block
    // within 150ms, that block's onEnter cancels this timer and re-targets
    // without an intermediate "nothing hovered" flash.
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      leaveTimerRef.current = null;
      activeRef.current = false;
      setHoveredId(null);
      setHoveredChainGroupId(null);
    }, 150);
  }, []);

  const styleFor = useCallback(
    (block) => {
      if (!hoveredId) {
        return {
          opacity: 1,
          transition: "opacity 0.3s ease",
        };
      }
      const blockId = block?.item?.calendarItem?.id || null;
      const blockChainId =
        block?.item?.calendarItem?.chainGroupId &&
        (block?.item?.calendarItem?.eventType || "job") !== "vacation"
          ? String(block.item.calendarItem.chainGroupId)
          : null;
      const isHoveredSelf = blockId != null && blockId === hoveredId;
      const isInHoveredChain =
        !!hoveredChainGroupId && blockChainId === hoveredChainGroupId;
      const shouldHighlight = isHoveredSelf || isInHoveredChain;
      const style = {
        opacity: shouldHighlight ? 1 : 0.4,
        transition: "opacity 0.3s ease",
      };
      if (isHoveredSelf) {
        style.zIndex = 51;
      } else if (isInHoveredChain) {
        style.zIndex = 50;
      }
      return style;
    },
    [hoveredId, hoveredChainGroupId]
  );

  return { onEnter, onLeave, styleFor, isHoverActive: hoveredId !== null };
}
